import axios from "axios";
import client from "./client";
import { showToast } from "../utils/toastUtils";

// Set the base URL for the API
const api = client();
const API_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

// Function to get data (GET request)
export const getData = async (endpoint) => {
  try {
    const response = await api.get(endpoint);
    return response.data;
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};

// Function to create new data (POST request)
export const postData = async (endpoint, payload) => {
  try {
    const response = await api.post(endpoint, payload);
    return response.data;
  } catch (error) {
    console.error("Error creating data:", error);
    if (error.response?.status === 403) {
      showToast("You are not authorized to perform this action", "error");
    }
    return error.response?.data;
  }
};

// Function to update data (PUT request)
export const putData = async (endpoint, payload) => {
  try {
    const response = await api.put(endpoint, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating data:", error);
    if (error.response?.status === 403) {
      showToast("You are not authorized to perform this action", "error");
    } 
    return error.response?.data;
  }
};
// Function to update data (PATCH request)
export const patchData = async (endpoint, payload) => {
  try {
    const response = await api.patch(endpoint, payload);
    return response.data;
  } catch (error) {
    console.error("Error updating data:", error);
    if (error.response?.status === 403) {
      showToast("You are not authorized to perform this action", "error");
    } 
    return error.response?.data;
  }
};

export const deleteData = async (endpoint) => {
  try {
    const response = await api.delete(endpoint);
    return response.data;
  } catch (error) {
    console.error("Error deleting data:", error);
    if (error.response?.status === 403) {
      showToast("You are not authorized to perform this action", "error");
    }
    return error.response?.data;
  }
};

// submit extra user details
export const submitExtraUserDetails = async (payload) => {
  const token = localStorage.getItem("token");
  const response = await axios.put(
    `${API_BASE_URL}/users/profile-details/`,
    payload,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  return response;
};

// get all institutes
export const getInstitutes = async () => {
  const response = await getData("/users/institutes/");
  return response;
};

// get all countries
export const getCountries = async () => {
  const response = await getData("/users/countries/");
  return response;
};

// get user details
export const getUserProfile = async () => {
  return await getData("/users/profile-details/");
};

// get user activities
export const getUserActivities = async () => {
  const response = await getData("/users/activities/");
  return response;
};

// get all journals
export const getJournals = async (searchTerm = "") => {
  const query = searchTerm ? `?query=${encodeURIComponent(searchTerm)}` : "";
  return await getData(`/journals/${query}`);
};

// upload paper (used in dropzone)
export const uploadPaper = async (payload) => {
  const response = await postData(`/papers/`, payload);
  return response;
};

// get paper insights
export const getPaperInsights = async (paperId) => {
  const response = await getData(`/reviews/paper/${paperId}/`);
  return response;
};

// to update insight checkbox click
export const updateInsight = async (paperId, payload) => {
  const response = await patchData(`reviews/paper/${paperId}/`, payload);
  return response;
};

// get papers by their paper id
export const getPaperById = async (paperId) => {
  const response = await getData(`/papers/${paperId}`);
  return response;
};

// export const getMyAllPapers = async () => {
//   const response = await getData(`/papers/`);
//   return response;
// };

//Get All pages (with pagination)
export const getAllPapers = async (page = 1) => {
  const response = await getData(`/papers/?page=${page}&page_size=5`);
  return response;
};

// get all comments of a paper
export const getPaperComments = async (paperId) => {
  return await getData(`/papers/${paperId}/comments`);
};

// create new paper comment
export const createPaperComment = async (paperId, payload) => {
  return await postData(`/papers/${paperId}/comments`, payload);
};

// upload paper version
export const uploadPaperVersion = async (paperId, payload) => {
  return await postData(`/papers/${paperId}/versions/`, payload);
};

export const deletePaper = async (paperId) => {
  const response = await deleteData(`/papers/${paperId}`);
  return response;
};

export const uploadSupplementaryMaterials = async (paperId) => {
  const response = await postData(`/papers/${paperId}/supplementary-materials`);
  return response;
};

// remove this function
export const deleteSupplementaryMaterial = async (paperId, Material_id) => {
  const response = await deleteData(
    `papers/${paperId}/supplementary-materials/${Material_id}/`
  );
  return response;
};

// get postcheck by paper id
export const getPostChecks = async (paperId) => {
  const response = await getData(`papers/${paperId}/postchecks/`);
  return response;
};

// submit checkbox values to backend
export const updatePostCheck = async (paperId, payload) => {
  const response = await putData(`papers/${paperId}/postchecks/`, payload);
  return response;
};

// stop AI checkings
export const stopAICheck = async (execution_id) => {
  const response = await postData(
    `/reviews/ai-agent?execution_id=${execution_id}&stop=true`,
    {}
  );
  return response;
};

// // Get all notifications
// export const getAllUserNotifications = async () => {
//   const response = await getData(`/users/notifications`);
//   return response;
// };
//get all notifications (with pagination)
export const getAllUserNotifications = async (page = 1, page_size = 5) => {
  const response = await getData(
    `/users/notifications/?page=${page}&page_size=${page_size}`
  );
  return response;
};

// update read state of one notification
export const updateOneNotificationReadStatus = async (notification_id) => {
  const response = await patchData(
    `/users/notifications/${notification_id}/mark-read`
  );
  return response;
};

// update read state of all notifications
export const updateAllNotificationReadStatus = async () => {
  const response = await patchData(`/users/notifications/mark-all-read`);
  return response;
};

// update read state of all notifications
export const updateProject = async (project_id, payload) => {
  const response = await putData(`/projects/${project_id}`, payload);
  return response;
};

// get project papers by project id
export const getProjectPapers = async (
  project_id,
  page = 1,
  pageSize = 10,
  status = null
) => {
  const statusParam = status && status !== "all" ? `&status=${status}` : "";
  return await getData(
    `/projects/${project_id}/papers/?page=${page}&page_size=${pageSize}${statusParam}`
  );
};

// create new project
export const createProject = async (payload) => {
  return await postData(`projects/`, payload);
};

// delete project by project id
export const deleteProject = async (project_id) => {
  const response = await deleteData(`projects/${project_id}`);
  return response;
};

// create new initiative
export const createInitiative = async (payload) => {
  const response = await postData(`initiatives/`, payload);
  return response;
};

// get all work streams by project id with pagination
export const getAllWorkStreamsByProjectId = async (
  project_id,
  page = 1,
  pageSize = 10
) => {
  return await getData(
    `projects/${project_id}/initiatives/?page=${page}&page_size=${pageSize}`
  );
};

// get WorkStream by id
export const getWorkStreamById = async (workStream_id) => {
  return await getData(`/initiatives/${workStream_id}`);
};

// create new WorkStreams = old name initiative
export const getAllWorkStreams = async (project_id) => {
  return await getData(`projects/${project_id}`);
};

// add member to project as admin or contributor
export const addMemberToProject = async (project_id, payload) => {
  const response = await putData(`projects/${project_id}/members`, payload);
  return response;
};

// remove member from the project
export const removeMemberFromProject = async (project_id, user_id) => {
  const response = await deleteData(
    `projects/${project_id}/members/${user_id}`
  );
  return response;
};

// delete initiative by initiative id
export const deleteInitiative = async (initiative_id) => {
  const response = await deleteData(`initiatives/${initiative_id}`);
  return response;
};

// ----------------------------------------------------------------------------------
// Generate task from given goal
export const generateTaskFromGoal = async (goal_id) => {
  const response = await getData(`/tasks/${goal_id}/generate-task`);
  return response;
};

// Helper to fetch tasks with given parameters
// Single function to fetch tasks
export const getTasks = async ({
  project_id,
  organization_id,
  workStream_id,
  priority,
  assignee,
} = {}) => {
  const params = {
    project_id,
    organization_id,
    workStream_id,
    priority,
    assignee,
  };

  // Remove undefined or null parameters
  const queryString = new URLSearchParams(
    Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null))
  ).toString();

  return getData(`/tasks/?${queryString}`);
};

// delete task by task id
export const deleteTask = async (task_id) => {
  const response = await deleteData(`/tasks/${task_id}/`);
  return response;
};

// update task by task id
export const createTask = async (payload) => {
  const response = await postData(`/tasks/`, payload);
  return response;
};

// update task by task id
export const updateTask = async (task_id, payload) => {
  const response = await putData(`/tasks/${task_id}/`, payload);
  return response;
};

// delete task by task id
export const deleteSubTask = async (task_id, subtask_id) => {
  const response = await deleteData(`/tasks/${task_id}/subtask/${subtask_id}/`);
  return response;
};

// create task by task id
export const createSubTask = async (task_id, payload) => {
  const response = await postData(`/tasks/${task_id}/subtask/`, payload);
  return response;
};

// create task bulks by goal id
export const createSubTaskBulk = async (task_id, payload) => {
  const response = await postData(
    `/tasks/${task_id}/subtask/?bulk=true`,
    payload
  );
  return response;
};

// update task by task id
export const updateSubTask = async (task_id, subtask_id, payload) => {
  const response = await putData(
    `/tasks/${task_id}/subtask/${subtask_id}/`,
    payload
  );
  return response;
};

// Get all task comments
export const getAllTaskComments = async (id) => {
  return await getData(`/tasks/comments/${id}`);
};

// Add comment
export const createTaskComment = async (id, payload) => {
  return await postData(`/tasks/comments/${id}`, payload);
};
// Add comment
export const changeTaskState = async (payload) => {
  return await patchData(`/tasks/status`, payload);
};

// list all paper statuses
export const getPaperStatuses = async () => {
  return await getData(`/papers/status/`);
};

// update paper status
export const updatePaperStatus = async (paperId, statusId, name) => {
  return await patchData(`/papers/${paperId}/status/`, {
    status_id: statusId,
    status: name,
  });
};

// list all paper statuses
export const getPaperTypes = async (id) => {
  return await getData(`/journals/${id}/paper-types`);
};

// request otp code
export const requestOTP = async (payload) => {
  return await postData(`/users/reset-password/request-otp/`, payload);
};

// verify otp code
export const verifyOTP = async (payload) => {
  return await postData(`/users/reset-password/validate-otp/`, payload);
};

// update password
export const updatePassword = async (payload) => {
  return await patchData(`/users/reset-password/change-password/`, payload);
};

// -----------------------------------------------------------------------------------
// create new organization
export const createOrganization = async (payload) => {
  return await postData(`/organizations/`, payload);
};

// get all organizations
export const getAllOrganizations = async () => {
  return await getData(`/organizations/`);
};

// update my current organization
export const updateCurrentOrganization = async (organization_id) => {
  return await patchData(`/organizations/${organization_id}/current/`);
};

// update organization by organization id
export const updateOrganization = async (organization_id, payload) => {
  return await putData(`/organizations/${organization_id}`, payload);
};

// delete organization by organization id
export const deleteOrganization = async (organization_id) => {
  return await deleteData(`/organizations/${organization_id}`);
};

// Get all papers of a organization by organization id
export const getOrganizationPapers = async (
  organization_id,
  page = 1,
  pageSize = 10
) => {
  return await getData(
    `/organizations/${organization_id}/papers/?page=${page}&page_size=${pageSize}`
  );
};

// Invite members to a organization
export const inviteMemberToOrganization = async (organization_id, payload) => {
  return await putData(`organizations/${organization_id}/members`, payload);
};

// Remove member from a organization
export const removeMemberFromOrganization = async (
  organization_id,
  user_id
) => {
  return await deleteData(
    `organizations/${organization_id}/members/${user_id}`
  );
};

// --------------------------------------------------------------------------------------
// create new Risk
export const createRisk = async (payload) => {
  return await postData(`/risks/`, payload);
};
// Get all risks
export const getAllRisks = async (organization_id) => {
  return await getData(`/risks/${organization_id}`);
};

// update risk by risk id
export const updateRisk = async (risk_id, payload) => {
  return await putData(`/risks/${risk_id}`, payload);
};

// delete a risk by risk id
export const deleteRisk = async (risk_id) => {
  return await deleteData(`/risks/${risk_id}`);
};

export const getAllRisksWithFilters = async (
  organization_id,
  workStream_id,
  created_by,
  project_id,
  task_id
) => {
  const params = new URLSearchParams();

  if (organization_id) params.append("organization_id", organization_id);
  if (workStream_id) params.append("workStream_id", workStream_id);
  if (created_by) params.append("created_by", created_by);
  if (project_id) params.append("project_id", project_id);
  if (task_id) params.append("task_id", task_id);

  return await getData(`/risks/?${params.toString()}`);
};
//------------------------------------------------------------------------------------------
// create new lesson
export const createLesson = async (payload) => {
  return await postData(`/lessons/`, payload);
};

// update lessons
export const updateLesson = async (lesson_id, payload) => {
  return await putData(`/lessons/${lesson_id}`, payload);
};

export const getAllLessonsWithParams = async (
  organization_id,
  workStream_id,
  created_by,
  project_id,
  task_id
) => {
  const params = new URLSearchParams();

  if (organization_id) params.append("organization_id", organization_id);
  if (workStream_id) params.append("workStream_id", workStream_id);
  if (created_by) params.append("created_by", created_by);
  if (project_id) params.append("project_id", project_id);
  if (task_id) params.append("task_id", task_id);

  return await getData(`/lessons/?${params.toString()}`);
};

// delete a risk by risk id
export const deleteLesson = async (lesson_id) => {
  return await deleteData(`/lessons/${lesson_id}`);
};

//--------------------------------------------------------------------------------------------

//get workstream related files
export const getWorkstreamFiles = async (workStream_id) => {
  return await getData(`/initiatives/${workStream_id}/files/`);
};

//delete workstream related files
export const deleteWorkstreamFiles = async (workStream_id, file_id) => {
  return await deleteData(`/initiatives/${workStream_id}/files/${file_id}`);
};

//-----------------------------------------------------------------------------------------

//get project related files
export const getProjectFiles = async (project_id) => {
  return await getData(`/projects/${project_id}/files/`);
};

//delete project related files
export const deleteProjectFiles = async (project_id, file_id) => {
  return await deleteData(`/projects/${project_id}/files/${file_id}`);
};

//-----------------------------------------------------------------------------------------

//add workstream related links
export const addWorkstreamLinks = async (workStream_id, payload) => {
  return await postData(`/initiatives/${workStream_id}/links/`, payload);
};

//get workstream related links
export const getWorkstreamLinks = async (workStream_id) => {
  return await getData(`/initiatives/${workStream_id}/links/`);
};

//update workstream related links
export const updateWorkstreamLinks = async (
  workStream_id,
  link_id,
  payload
) => {
  return await putData(
    `/initiatives/${workStream_id}/links/${link_id}`,
    payload
  );
};

//delete workstream related links
export const deleteWorkstreamLinks = async (workStream_id, link_id) => {
  return await deleteData(`/initiatives/${workStream_id}/links/${link_id}`);
};

//---------------------------------------------------------------------------------------------

//add project related links
export const addProjectLinks = async (project_id, payload) => {
  return await postData(`/projects/${project_id}/links/`, payload);
};
//get project related links
export const getProjectLinks = async (project_id) => {
  return await getData(`/projects/${project_id}/links/`);
};
//update project related links
export const updateProjectLinks = async (project_id, link_id, payload) => {
  return await putData(`/projects/${project_id}/links/${link_id}`, payload);
};

//delete project related links
export const deleteProjectLinks = async (project_id, link_id) => {
  return await deleteData(`/projects/${project_id}/links/${link_id}`);
};

//-----------------------------------------------------------------------------------------
// add note for a workstream
export const addNoteToWorkStream = async (workStream_id, payload) => {
  return await postData(`/initiatives/${workStream_id}/notes/`, payload);
};

// get all notes of a workstream
export const getWorkStreamNotes = async (workStream_id) => {
  return await getData(`/initiatives/${workStream_id}/notes/`);
};

// delete note by note id
export const deleteWorkStreamNote = async (workStream_id, note_id) => {
  return await deleteData(`/initiatives/${workStream_id}/notes/${note_id}`);
};

// update note by note id
export const updateWorkStreamNote = async (workStream_id, note_id, payload) => {
  return await putData(
    `/initiatives/${workStream_id}/notes/${note_id}`,
    payload
  );
};

//-----------------------------------------------------------------------------------------
// add note for a project
export const addNoteToProject = async (project_id, payload) => {
  return await postData(`/projects/${project_id}/notes/`, payload);
};

// get all notes of a project
export const getProjectNotes = async (project_id) => {
  return await getData(`/projects/${project_id}/notes/`);
};

// delete note by note id
export const deleteProjectNote = async (project_id, note_id) => {
  return await deleteData(`/projects/${project_id}/notes/${note_id}`);
};

// update note by note id
export const updateProjectNote = async (project_id, note_id, payload) => {
  return await putData(`/projects/${project_id}/notes/${note_id}`, payload);
};

//-----------------------------------------------------------------------------------------

//get Activity log

// export const getUserActivity = async () => {
//   return await getData(`/users/activity_data/`);
// };

// export const getProjectActivities = async (project_id) => {
//   return await getData(`/projects/${project_id}/activities/`);
// };

// export const getWorkStreamActivities = async (workStream_id) => {
//   return await getData(`/initiatives/${workStream_id}/activities/`);
// };

// Get user activity data (with pagination)
export const getUserActivity = async (page = 1) => {
  return await getData(`/users/activity_data/?page=${page}`);
};

// Get project activities (with pagination)
export const getProjectActivities = async (project_id, page = 1) => {
  return await getData(`/projects/${project_id}/activities/?page=${page}`);
};

// Get workstream activities (with pagination)
export const getWorkStreamActivities = async (workStream_id, page = 1) => {
  return await getData(
    `/initiatives/${workStream_id}/activities/?page=${page}`
  );
};

// -------------------------------------------------------------------------------------
// get all analytics of the organization
export const getOrganizationAnalytics = async (organization_id) => {
  return await getData(`/organizations/${organization_id}/analytics/`);
};

//------------------------------------------------------------------------------------------
// get al alerts
export const getAllAlerts = async () => {
  return await getData(`/alerts/`);
};

//-------------------------------------------------------------------------------------------
//get custom fields for a goal

export const getCustomFields = async (task_id) => {
  return await getData(`/projects/${task_id}/custom_fields/`);
};

//add custom field for a goal
export const addCustomField = async (task_id, payload) => {
  return await postData(`/projects/${task_id}/custom_fields/`, payload);
};

//delete custom field by field id
export const deleteCustomField = async (project_id, field_id) => {
  return await deleteData(`/projects/${project_id}/custom_fields/${field_id}`);
};

//update custom field by field id
export const updateCustomField = async (project_id, field_id, payload) => {
  return await putData(
    `/projects/${project_id}/custom_fields/${field_id}`,
    payload
  );
};

//-------------------------------------------------------------------------------------------

//get riskTemplates

export const getRiskTemplates = async () => {
  return await getData(`/risks/templates/`);
};

//add riskTemplate
export const addRiskTemplate = async (payload) => {
  return await postData(`/risks/templates/`, payload);
};

//delete riskTemplate by template id
export const deleteRiskTemplate = async (template_id) => {
  return await deleteData(`/risks/templates/${template_id}`);
};

//update riskTemplate by template id
export const updateRiskTemplate = async (template_id, payload) => {
  return await putData(`/risks/templates/${template_id}`, payload);
};

//-------------------------------------------------------------------------------------------

//get lessons templates
export const getLessonTemplates = async () => {
  return await getData(`/lessons/templates/`);
};

//add lesson template
export const addLessonTemplate = async (payload) => {
  return await postData(`/lessons/templates/`, payload);
};

//delete lesson template by template id
export const deleteLessonTemplate = async (template_id) => {
  return await deleteData(`/lessons/templates/${template_id}`);
};

//update lesson template by template id
export const updateLessonTemplate = async (template_id, payload) => {
  return await putData(`/lessons/templates/${template_id}`, payload);
};

export const getJournalList = async (searchTerm = "") => {
  const query = searchTerm ? `?search=${encodeURIComponent(searchTerm)}` : "";
  return await getData(`/journals/journal-list/${query}`);
};

//send journal select email
export const sendJournalSelectEmail = async (journalId, payload = {}) => {
  return await postData(`/journals/${journalId}/request/`, payload);
};

// update workstream by workstream id
export const updateWorkstream = async (workstream_id, payload) => {
  return await putData(`/initiatives/${workstream_id}`, payload);
};

//submit feedback for Insight
export const submitFeedback = async (paper_id, insight_id, payload) => {
  return await postData(
    `/reviews/paper/${paper_id}/insight/${insight_id}/feedback/`,
    payload
  );
};

//update user profile
export const updateUserProfile = async (payload) => {
  return await putData(`/users/update-profile/`, payload);
};

//update project status
export const updateProjectStatus = async (project_id, payload) => {
  return await putData(`/projects/${project_id}/status/`, payload);
};

//update project budget
export const updateProjectBudget = async (project_id, payload) => {
  return await putData(`/projects/${project_id}/budget/`, payload);
};
//---------------------------------------------------------------------------------
//Explore Reserach Papers (OpenAlex)
export const searchResearchPapers = async (
  searchTerm = "",
  page = 1,
  perPage = 25,
  publicationYear = "",
  institution = "",
  author = "",
  workType = "",
  groupBy = null,
  filterString = null
) => {
  const params = new URLSearchParams();
  if (searchTerm.trim()) {
    params.append('q', searchTerm);
  }
  params.append('page', page);
  params.append('per_page', perPage);
  if (publicationYear) {
    params.append('publication_year', publicationYear);
  }
  if (institution) {
    params.append('institution', institution);
  }
  if (author) {
    params.append('author', author);
  }
  if (workType) {
    params.append('type', workType);
  }
  if (groupBy) {
    params.append('group_by', groupBy);
  }
  if (filterString) {
    params.append('filter', filterString);
  }
  const queryString = params.toString();
  const endpoint = `papers/open-alex/${queryString ? `?${queryString}` : ''}`;
  
  return await getData(endpoint);
};

// --------------------------------------------------------------------------
// Journal Recommender
export const generateAbstractWithAI = async (payload) => {
  return await postData(`/papers/generate-abstract/`, payload);
};

export const getRecommendedJournals = async (payload) => {
  return await postData(`/journals/recommender/`, payload);
};

export const voteToJournal = async (journalId,payload) => {
  return await patchData(`/journals/${journalId}/vote/`, payload);
};


// --------------------------------------------------------------------------
// Conference Recommender
export const getRecommendedConferences = async (payload) => {
  return await postData(`/users/recommended-conferences/`, payload);
};

export const voteToConferences = async (payload) => {
  return await patchData(`/users/recommended-conferences/vote/`, payload);
};

// --------------------------------------------------------------------------
// Research Spotlight Tools Voting
export const voteToResearchSpotlightTool = async (payload) => {
  return await patchData(`/users/upcoming-tools/vote/`, payload);
};

export const getResearchSpotlightToolVotes = async () => {
  return await getData(`/users/upcoming-tools/vote/`);
};


// Get reminders for a user
export const getReminders = async (userId) => {
  return await getData(`/users/reminders/?user_id=${userId}`);
};

// Get a specific reminder
export const getReminder = async (id) => {
  return await getData(`/users/reminders/?id=${id}`);
};

// Create a new reminder
export const createReminder = async (reminderData) => {
  return await postData('/users/reminders/', reminderData);
};

// Delete a reminder
export const deleteReminder = async (id) => {
  return await deleteData(`/users/reminders/?id=${id}`);
};